import {
  formatDatePreview,
  type DateMode,
  type DateTimeValidation,
  type DefaultTimeOption,
  type FutureSub,
  type Meridian,
  type TimeFormat,
  type TimeMode,
  type WeekdayMap,
} from "@/util/dateTimeSerialize";
import CreditCardShield from "@/assets/icons/credit-card-shield.svg?url";
import AddressIcon from "@/assets/icons/custom-builder-address.svg?url";
import BirthdayIcon from "@/assets/icons/custom-builder-birthdate.svg?url";
import ButtonIcon from "@/assets/icons/custom-builder-button.svg?url";
import CaptchaIcon from "@/assets/icons/custom-builder-captcha.svg?url";
import CheckboxIcon from "@/assets/icons/custom-builder-checkbox.svg?url";
import CityIcon from "@/assets/icons/custom-builder-city.svg?url";
import CountryIcon from "@/assets/icons/custom-builder-country.svg?url";
import DatePickerIcon from "@/assets/icons/custom-builder-date-picker.svg?url";
import DropdownIcon from "@/assets/icons/custom-builder-dropdown.svg?url";
import EmailIcon from "@/assets/icons/custom-builder-email.svg?url";
import FileUploadIcon from "@/assets/icons/custom-builder-file-upload.svg?url";
import FirstNameIcon from "@/assets/icons/custom-builder-first-name.svg?url";
import FullNameIcon from "@/assets/icons/custom-builder-full-name.svg?url";
import HtmlIcon from "@/assets/icons/custom-builder-html.svg?url";
import ImageIcon from "@/assets/icons/custom-builder-image.svg?url";
import LastNameIcon from "@/assets/icons/custom-builder-last-name.svg?url";
import MonetaryIcon from "@/assets/icons/custom-builder-monetary.svg?url";
import MultiLineIcon from "@/assets/icons/custom-builder-multi-line.svg?url";
import OrganizationIcon from "@/assets/icons/custom-builder-organization.svg?url";
import PhoneIcon from "@/assets/icons/custom-builder-phone.svg?url";
import PostalCodeIcon from "@/assets/icons/custom-builder-postal-code.svg?url";
import RadioIcon from "@/assets/icons/custom-builder-radio.svg?url";
import SignatureIcon from "@/assets/icons/custom-builder-signature.svg?url";
import SingleLineIcon from "@/assets/icons/custom-builder-single-line.svg?url";
import SourceIcon from "@/assets/icons/custom-builder-source.svg?url";
import StateIcon from "@/assets/icons/custom-builder-state.svg?url";
import TermsAndConditionsIcon from "@/assets/icons/custom-builder-terms.svg?url";
import TextBoxIcon from "@/assets/icons/custom-builder-text-box.svg?url";
import TextIcon from "@/assets/icons/custom-builder-text.svg?url";
import WebsiteIcon from "@/assets/icons/custom-builder-website.svg?url";
import SellProductIcon from "@/assets/icons/sell-product.svg?url";
import { Star01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { h, nextTick } from "vue";
import { i18n } from "../plugin/vue-i18n";
import {
  DEFAULT_FOOTER_SHADOW,
  DEFAULT_LABEL_COLOR,
  DEFAULT_TEXT_COLOR,
  FULL_WIDTH_FIELDS,
  LEFT,
  LEFT_AND_RIGHT,
  ONLY_ARROW,
  ONLY_TEXT,
  RATING_ELEMENT_DEFAULTS,
  RIGHT,
  TERMS_AND_CONDITIONS_LABEL,
  TEXT_AND_ARROW,
} from "./constants";
import {
  defaultBorder,
  defaultBtnPadding,
  defaultPadding,
  defaultShadow,
} from "./default";
import { isElementVisible, selectAllText } from "./focusNavigation";
import {
  buildPictureChoiceCustomFieldPayload,
  createDefaultPictureChoiceElement,
  isPictureChoiceField,
} from "./pictureChoice";
import { normalizeUrlValidation, type UrlValidation } from "./urlField";

export {
  addActiveTabLoopListener,
  handleActiveElementTabLoop,
  removeActiveTabLoopListener,
} from "./focusNavigation";

const { t } = i18n.global;
export interface GroupField {
  id?: string;
  label: string;
  /** Custom-field display name (editable via the Date & Time "Custom field name" input). */
  name?: string;
  tag: string;
  hiddenFieldQueryKey?: string;
  type: string;
  placeholder?: string;
  required?: boolean;
  /** Payment fields only — require a credit card even when the order total is 0. */
  requireCreditCard?: boolean;
  standard: boolean;
  custom?: boolean;
  icon: any;
  count?: number;
  iconType?: string;
  iconAlignment?: string;
  lowestRating?: string;
  highestRating?: string;
  calculationType?: string;
  category: string;
  dataType?: string;
  typeLabel?: string;
  subType?: string;
  format?: string;
  separator?: string;
  enableCountryPicker?: boolean;
  picklistOptions?: Array<{ label: string; prefillValue?: string }> | string[];
  title?: string;
  children?: GroupField[];
  color?: string;
  fontFamily?: string;
  fontSize?: number;
  weight?: number;
  html?: string;
  value?: string;
  bgColor?: string;
  align?: string;
  shadow?: any;
  textColor?: string;
  linkColor?: string;
  padding?: any;
  preview?: string;
  border?: any;
  hidden?: boolean;
  shortLabel?: string;
  fieldKey?: string;
  hiddenFieldValue?: string;
  submitSubText?: string;
  hideInLeftSideBar?: boolean;
  borderType?: string;
  borderColor?: string;
  borderRadius?: number;
  fullwidth?: boolean;
  subTextColor?: string;
  subTextFontSize?: number;
  subTextWeight?: number;
  subTextFontFamily?: string;
  radius?: number;
  webkitBoxShadow?: string;
  boxShadow?: string;
  labelAlignment?: AlignmentPosition;
  urlValidation?: UrlValidation;
  invisible?: boolean;
  // --- Date & Time Picker field (custom DATE/TIME/DATE_TIME) ---
  customEdited?: boolean;
  /** Set when location custom-field metadata changed, gating the update API on save. */
  customfieldUpdated?: boolean;
  disablePicker?: boolean;
  dateTimeValidation?: DateTimeValidation;
  dateMode?: DateMode;
  futureSub?: FutureSub;
  bufferDays?: number | string;
  rollingDays?: number | string;
  dateRangeMin?: string;
  dateRangeMax?: string;
  days?: WeekdayMap;
  timeMode?: TimeMode;
  timeRangeStart?: string;
  timeRangeStartMeridian?: Meridian;
  timeRangeEnd?: string;
  timeRangeEndMeridian?: Meridian;
  timeFormat?: TimeFormat;
  defaultTime?: DefaultTimeOption;
  customTime?: string;
  customTimeMeridian?: Meridian;
  daysAllowed?: number[];
}

export enum AlignmentPosition {
  LEFT = "left",
  RIGHT = "right",
  DEFAULT = "default", // default means Form/survey Level style settings takes precedence over the element level settings
  TOP = "top",
}

interface FieldGroup {
  label: string;
  group: GroupField[];
}

interface StandardFields {
  quizQuestions: FieldGroup;
  personal: FieldGroup;
  submit: FieldGroup;
  integration: FieldGroup;
  address: FieldGroup;
  text: FieldGroup;
  choiceElements: FieldGroup;
  rating: FieldGroup;
  customized: FieldGroup;
  other: FieldGroup;
}

/** "New" badge rendered inside a sidebar field icon (see the Rating field). */
const newFieldBadge = () =>
  h(
    "span",
    {
      style: {
        position: "absolute",
        top: "-10px",
        right: "32px",
        padding: "2px 6px",
        fontSize: "10px",
        fontFamily: "Inter",
        fontStyle: "normal",
        fontWeight: 500,
        color: "#004EEB",
        lineHeight: "18px" /* 150% */,
        borderRadius: "16px",
        background: " #EFF4FF",
      },
    },
    t("common.new")
  );

export const standardFields: StandardFields = {
  quizQuestions: {
    label: t("common.quizQuestions"),
    group: [
      {
        label: t("fields.quizQuestions.singleChoice"),
        tag: "radio",
        hiddenFieldQueryKey: "radio",
        type: "radio",
        dataType: "RADIO",
        typeLabel: t("fields.quizQuestions.singleChoice"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: RadioIcon }),
        category: "quizQuestions",
        picklistOptions: ["Option 1", "Option 2", "Option 3"],
      },
      {
        label: t("fields.quizQuestions.multipleChoice"),
        tag: "checkbox",
        hiddenFieldQueryKey: "checkbox",
        type: "checkbox",
        dataType: "CHECKBOX",
        typeLabel: t("fields.quizQuestions.multipleChoice"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: CheckboxIcon }),
        category: "quizQuestions",
        picklistOptions: ["Option 1", "Option 2", "Option 3"],
      },
      {
        label: t("fields.quizQuestions.yesNo"),
        tag: "radio",
        hiddenFieldQueryKey: "radio",
        type: "radio",
        dataType: "RADIO",
        typeLabel: t("fields.quizQuestions.yesNo"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: RadioIcon }),
        category: "quizQuestions",
        picklistOptions: [t("common.yes"), t("common.no")],
      },
      {
        label: t("fields.quizQuestions.shortAnswer"),
        tag: "single_line",
        hiddenFieldQueryKey: "single_line",
        type: "text",
        dataType: "TEXT",
        typeLabel: t("fields.quizQuestions.shortAnswer"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: SingleLineIcon }),
        category: "text",
      },
      {
        label: t("fields.quizQuestions.longAnswer"),
        tag: "multi_line",
        hiddenFieldQueryKey: "multi_line",
        type: "large_text",
        dataType: "LARGE_TEXT",
        typeLabel: t("fields.quizQuestions.longAnswer"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: MultiLineIcon }),
        category: "text",
      },
      {
        label: t("fields.quizQuestions.number"),
        tag: "text",
        hiddenFieldQueryKey: "number",
        type: "numerical",
        dataType: "NUMERICAL",
        typeLabel: t("fields.quizQuestions.number"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: MonetaryIcon }),
        category: "other",
      },
      {
        label: t("fields.quizQuestions.singleDropdown"),
        tag: "dropdown",
        hiddenFieldQueryKey: "dropdown",
        type: "single_options",
        dataType: "SINGLE_OPTIONS",
        typeLabel: t("fields.quizQuestions.singleDropdown"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: DropdownIcon }),
        category: "choiceElements",
        picklistOptions: ["Option 1", "Option 2", "Option 3"],
      },
      {
        label: t("fields.quizQuestions.multiDropdown"),
        tag: "dropdown_multi",
        hiddenFieldQueryKey: "dropdown_multi",
        type: "multiple_options",
        dataType: "MULTIPLE_OPTIONS",
        typeLabel: t("fields.quizQuestions.multiDropdown"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: DropdownIcon }),
        category: "choiceElements",
        picklistOptions: ["Option 1", "Option 2", "Option 3"],
      },
    ],
  },
  personal: {
    label: `${t("common.personalInfo")}`,
    group: [
      {
        label: t("fields.personal.fullName"),
        tag: "full_name",
        hiddenFieldQueryKey: "full_name",
        type: "text",
        typeLabel: "Text",
        placeholder: t("fields.personal.fullNamePlaceholder"),
        required: false,
        standard: true,
        icon: () => h("img", { src: FullNameIcon }),
        category: "personal",
      },
      {
        label: t("fields.personal.firstName"),
        tag: "first_name",
        hiddenFieldQueryKey: "first_name",
        type: "text",
        typeLabel: "Text",
        placeholder: t("fields.personal.firstNamePlaceholder"),
        required: false,
        standard: true,
        icon: () => h("img", { src: FirstNameIcon }),
        category: "personal",
      },
      {
        label: t("fields.personal.lastName"),
        tag: "last_name",
        hiddenFieldQueryKey: "last_name",
        type: "text",
        typeLabel: "Text",
        placeholder: t("fields.personal.lastNamePlaceholder"),
        required: false,
        standard: true,
        icon: () => h("img", { src: LastNameIcon }),
        category: "personal",
      },
      {
        label: t("fields.personal.dateOfBirth"),
        tag: "date_of_birth",
        type: "date",
        typeLabel: "Date",
        format: "YYYY-MM-DD",
        separator: "-",
        placeholder: t("fields.personal.dateOfBirthPlaceholder"),
        standard: true,
        icon: () => h("img", { src: BirthdayIcon }),
        category: "personal",
      },
      {
        label: t("fields.personal.phone"),
        tag: "phone",
        hiddenFieldQueryKey: "phone",
        type: "text",
        typeLabel: "Phone",
        placeholder: t("fields.personal.phonePlaceholder"),
        enableCountryPicker: false,
        required: true,
        standard: true,
        icon: () => h("img", { src: PhoneIcon }),
        category: "personal",
      },
      {
        label: t("fields.personal.email"),
        tag: "email",
        hiddenFieldQueryKey: "email",
        type: "email",
        typeLabel: "Email",
        placeholder: "your@email.com",
        required: true,
        standard: true,
        icon: () => h("img", { src: EmailIcon }),
        category: "personal",
      },
    ],
  },
  submit: {
    label: `${t("common.submit")}`,
    group: [
      {
        label: t("common.submit"),
        tag: "button",
        hiddenFieldQueryKey: "button",
        type: "submit",
        placeholder: t("fields.submit.button"),
        submitSubText: "",
        bgColor: "155EEFFF",
        padding: defaultBtnPadding,
        border: 0,
        borderType: "none",
        borderColor: "FFFFFF",
        borderRadius: 6,
        shadow: defaultShadow,
        fullwidth: true,
        color: "FFFFFF",
        weight: 500,
        radius: 4,
        subTextColor: "000000",
        subTextWeight: 200,
        align: "center",
        standard: true,
        icon: () => h("img", { src: ButtonIcon }),
        category: "submit",
      },
    ],
  },
  integration: {
    label: `${t("common.payment.payments")}`,
    group: [
      {
        label: t("fields.integration.sellProducts"),
        tag: "payment",
        type: "payment",
        subType: "product",
        icon: () => h("img", { src: SellProductIcon }),
        category: "payment",
        required: true,
        requireCreditCard: true,
        standard: true,
      },
      {
        label: t("fields.integration.collectPayment"),
        tag: "payment",
        type: "payment",
        subType: "donation",
        icon: () => h("img", { src: CreditCardShield }),
        category: "payment",
        required: true,
        standard: true,
      },
    ],
  },
  address: {
    label: `${t("common.address")}`,
    group: [
      {
        label: t("fields.address.address"),
        tag: "group_address",
        hiddenFieldQueryKey: "addressId",
        type: "group",
        placeholder: t("fields.address.addressPlaceholder"),
        required: false,
        standard: true,
        icon: () =>
          h(
            "div",
            {
              style: { display: "inline-block", padding: "0px !important" },
              class: "w-[60px] h-[60px] p-[0px]",
            },
            [
              h(
                "span",
                {
                  style: {
                    position: "absolute",
                    top: "-10px",
                    right: "20px",
                    padding: "2px 6px",
                    fontSize: "10px",
                    fontFamily: "Inter",
                    fonSize: "12px",
                    fontStyle: "normal",
                    fontWeight: 500,
                    color: "#004EEB",
                    lineHeight: "18px" /* 150% */,
                    borderRadius: "16px",
                    background: " #EFF4FF",
                  },
                },
                t("common.updated")
              ),
              h("img", { src: AddressIcon, class: "p-2" }),
            ]
          ),
        category: "address",
        title: t("fields.address.streetAddress"),
      },
      {
        label: t("fields.address.streetAddress"),
        tag: "address",
        hiddenFieldQueryKey: "address",
        type: "text",
        typeLabel: "Text",
        placeholder: t("fields.address.streetAddressPlaceholder"),
        required: false,
        standard: true,
        icon: () => h("img", { src: AddressIcon }),
        category: "address",
        title: t("fields.address.address"),
        hideInLeftSideBar: true,
      },
      {
        label: t("fields.address.city"),
        tag: "city",
        hiddenFieldQueryKey: "city",
        type: "text",
        typeLabel: "Text",
        placeholder: t("fields.address.cityPlaceholder"),
        required: false,
        standard: true,
        icon: () => h("img", { src: CityIcon }),
        category: "address",
        title: t("fields.address.city"),
      },
      {
        label: t("fields.address.state"),
        tag: "state",
        hiddenFieldQueryKey: "state",
        type: "text",
        typeLabel: "Text",
        placeholder: t("fields.address.statePlaceholder"),
        required: false,
        standard: true,
        icon: () => h("img", { src: StateIcon }),
        category: "address",
        title: t("fields.address.state"),
      },
      {
        label: t("fields.address.country"),
        tag: "country",
        hiddenFieldQueryKey: "country",
        type: "select",
        typeLabel: "Select",
        placeholder: t("fields.address.countryPlaceholder"),
        required: false,
        standard: true,
        icon: () => h("img", { src: CountryIcon }),
        category: "address",
        title: t("fields.address.country"),
      },
      {
        label: t("fields.address.postalCode"),
        tag: "postal_code",
        hiddenFieldQueryKey: "postal_code",
        type: "text",
        typeLabel: "Text",
        placeholder: t("fields.address.postalCodePlaceholder"),
        required: false,
        standard: true,
        icon: () => h("img", { src: PostalCodeIcon }),
        category: "address",
        title: t("fields.address.postalCode"),
      },
      {
        label: t("fields.address.organization"),
        tag: "organization",
        hiddenFieldQueryKey: "organization",
        type: "text",
        typeLabel: "Text",
        placeholder: t("fields.address.organizationPlaceholder"),
        required: false,
        standard: true,
        icon: () => h("img", { src: OrganizationIcon }),
        category: "address",
        title: t("fields.address.organization"),
      },
      {
        label: t("fields.address.website"),
        tag: "website",
        hiddenFieldQueryKey: "website",
        type: "text",
        typeLabel: "Text",
        placeholder: t("fields.address.websitePlaceholder"),
        standard: true,
        icon: () => h("img", { src: WebsiteIcon }),
        category: "address",
        title: t("fields.address.website"),
      },
    ],
  },
  text: {
    label: `${t("common.text")}`,
    group: [
      {
        label: t("fields.text.singleLine"),
        tag: "single_line",
        hiddenFieldQueryKey: "single_line",
        type: "text",
        dataType: "TEXT",
        typeLabel: t("fields.text.singleLine"),
        placeholder: t("fields.text.singleLinePlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: SingleLineIcon }),
        category: "text",
      },
      {
        label: t("fields.text.multiLine"),
        tag: "multi_line",
        hiddenFieldQueryKey: "multi_line",
        type: "large_text",
        dataType: "LARGE_TEXT",
        typeLabel: t("fields.text.multiLine"),
        placeholder: t("fields.text.multiLinePlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: MultiLineIcon }),
        category: "text",
      },
      {
        label: t("fields.text.textBoxList"),
        tag: "textbox_list",
        hiddenFieldQueryKey: "textbox_list",
        type: "textbox_list",
        dataType: "TEXTBOX_LIST",
        typeLabel: t("fields.text.textBoxList"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: TextBoxIcon }),
        category: "text",
        picklistOptions: [
          {
            label: "Option 1",
            prefillValue: "",
          },
          {
            label: "Option 2",
            prefillValue: "",
          },
          {
            label: "Option 3",
            prefillValue: "",
          },
        ],
      },
    ],
  },
  choiceElements: {
    label: t("common.choiceElements"),
    group: [
      {
        label: t("fields.choiceElements.singleDropdown"),
        tag: "dropdown",
        hiddenFieldQueryKey: "dropdown",
        type: "single_options",
        dataType: "SINGLE_OPTIONS",
        typeLabel: t("fields.choiceElements.singleDropdown"),
        placeholder: t("fields.choiceElements.singleDropdownPlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: DropdownIcon }),
        category: "choiceElements",
        picklistOptions: ["Option 1", "Option 2", "Option 3"],
      },
      {
        label: t("fields.choiceElements.multiDropdown"),
        tag: "dropdown_multi",
        hiddenFieldQueryKey: "dropdown_multi",
        type: "multiple_options",
        dataType: "MULTIPLE_OPTIONS",
        typeLabel: t("fields.choiceElements.multiDropdown"),
        placeholder: t("fields.choiceElements.multiDropdownPlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: DropdownIcon }),
        category: "choiceElements",
        picklistOptions: ["Option 1", "Option 2", "Option 3"],
      },
      {
        label: t("fields.choiceElements.checkbox"),
        tag: "checkbox",
        hiddenFieldQueryKey: "checkbox",
        type: "checkbox",
        dataType: "CHECKBOX",
        typeLabel: t("fields.choiceElements.checkbox"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: CheckboxIcon }),
        category: "choiceElements",
        picklistOptions: ["Option 1", "Option 2", "Option 3"],
      },
      {
        label: t("fields.choiceElements.radio"),
        tag: "radio",
        hiddenFieldQueryKey: "radio",
        type: "radio",
        dataType: "RADIO",
        typeLabel: t("fields.choiceElements.radio"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: RadioIcon }),
        category: "choiceElements",
        picklistOptions: ["Option 1", "Option 2", "Option 3"],
      },
      {
        ...createDefaultPictureChoiceElement(),
        label: t("fields.choiceElements.pictureChoice.label"),
        typeLabel: t("fields.choiceElements.pictureChoice.label"),
        icon: () => [
          h(
            "div",
            {
              style: { display: "inline-block", padding: "0px !important" },
              class: "p-[0px]",
            },
            [newFieldBadge()]
          ),
          h("img", { src: ImageIcon, class: "h-8 w-8 p-2" }),
        ],
        category: "choiceElements",
      },
    ],
  },
  rating: {
    label: t("common.rating.rating"),
    group: [
      {
        label: t("common.rating.rating"),
        tag: "rating",
        hiddenFieldQueryKey: "rating",
        type: "rating",
        dataType: "RATING",
        typeLabel: t("common.rating.rating"),
        placeholder: "",
        required: false,
        standard: false,
        custom: true,
        icon: () => [
          h(
            "div",
            {
              style: { display: "inline-block", padding: "0px !important" },
              class: "p-[0px]",
            },
            [newFieldBadge()]
          ),
          h(Star01Icon, {
            class: "h-8 w-8 p-2",
            style: {
              strokeWidth: "1.5",
            },
          }),
        ],
        category: "rating",
        ...RATING_ELEMENT_DEFAULTS,
      },
    ],
  },
  customized: {
    label: `${t("common.customized")}`,
    group: [
      {
        label: `<h1 style="padding-left: 0px!important;">${t(
          "fields.customized.text"
        )}</h1>`,
        tag: "header",
        hiddenFieldQueryKey: "header",
        type: "h1",
        placeholder: "header",
        typeLabel: "Text",
        weight: 400,
        bgColor: "FFFFFF00",
        align: "left",
        shadow: defaultShadow,
        padding: defaultPadding,
        border: defaultBorder,
        standard: true,
        icon: () => h("img", { src: TextIcon }),
        category: "customized",
      },
      {
        label: t("fields.customized.html"),
        tag: "html",
        hiddenFieldQueryKey: "html",
        type: "html",
        placeholder: t("fields.customized.htmlPlaceholder"),
        html: "",
        standard: true,
        icon: () => h("img", { src: HtmlIcon }),
        category: "customized",
      },
      {
        label: t("fields.customized.botProtection"),
        tag: "captcha",
        hiddenFieldQueryKey: "captcha",
        type: "captcha",
        standard: true,
        invisible: false,
        icon: () => h("img", { src: CaptchaIcon }),
        category: "customized",
      },
      {
        label: t("fields.customized.source"),
        tag: "source",
        hiddenFieldQueryKey: "source",
        type: "source",
        value: "",
        standard: true,
        icon: () => h("img", { src: SourceIcon }),
        category: "customized",
      },
      {
        label: t("fields.customized.termsAndConditions"),
        tag: "terms_and_conditions",
        type: "terms_and_conditions",
        required: true,
        hiddenFieldQueryKey: "terms_and_conditions",
        textColor: "000000",
        linkColor: "188bf6",
        placeholder: `<p style='font-family: Inter; font-size: 16px; font-weight: 400; color: ${
          TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR
        };'>${t("fields.customized.termsAndConditionsPlaceholder")}</p>`,
        preview: `<p style='font-family: Inter; font-size: 16px; font-weight: 400; color: ${
          TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR
        };'>${t("fields.customized.termsAndConditionsPlaceholder")}</p>`,
        standard: true,
        icon: h("img", { src: TermsAndConditionsIcon }),
        category: "customized",
      },
      {
        label: t("fields.customized.score"),
        tag: "score",
        hiddenFieldQueryKey: "score",
        dataType: "SCORE",
        typeLabel: t("fields.customized.score"),
        type: "score",
        placeholder: t("fields.customized.scorePlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: FileUploadIcon }),
        category: "other",
      },
    ],
  },
  other: {
    label: t("common.otherElements"),
    group: [
      {
        label: t("fields.other.image"),
        tag: "image",
        hiddenFieldQueryKey: "image",
        type: "img",
        placeholder: "",
        standard: true,
        icon: () => h("img", { src: ImageIcon }),
        category: "other",
      },
      {
        label: t("fields.other.fileUpload"),
        tag: "file_upload",
        hiddenFieldQueryKey: "file_upload",
        dataType: "FILE_UPLOAD",
        typeLabel: t("fields.other.fileUpload"),
        type: "file_upload",
        placeholder: t("fields.other.fileUploadPlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: FileUploadIcon }),
        category: "other",
      },
      {
        label: t("fields.other.monetary"),
        tag: "monetory",
        hiddenFieldQueryKey: "monetory",
        type: "monetory",
        dataType: "MONETORY",
        typeLabel: t("fields.other.monetary"),
        placeholder: t("fields.other.monetaryPlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: MonetaryIcon }),
        category: "other",
      },
      {
        label: t("fields.other.number"),
        tag: "text",
        hiddenFieldQueryKey: "number",
        type: "numerical",
        dataType: "NUMERICAL",
        typeLabel: t("fields.other.number"),
        placeholder: t("fields.other.numberPlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: MonetaryIcon }),
        category: "other",
      },
      {
        label: t("fields.other.datePicker"),
        tag: "date",
        hiddenFieldQueryKey: "date",
        type: "date",
        dataType: "DATE",
        typeLabel: t("fields.other.datePicker"),
        placeholder: formatDatePreview(),
        required: false,
        standard: false,
        custom: true,
        icon: () => [
          h(
            "div",
            {
              style: { display: "inline-block", padding: "0px !important" },
              class: "p-[0px]",
            },
            [newFieldBadge()]
          ),
          h("img", { src: DatePickerIcon, class: "h-8 w-8 p-2" }),
        ],
        category: "other",
      },
      {
        label: t("fields.other.signature"),
        tag: "signature",
        hiddenFieldQueryKey: "signature",
        type: "signature",
        dataType: "SIGNATURE",
        typeLabel: t("fields.other.signature"),
        placeholder: t("fields.other.signaturePlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => h("img", { src: SignatureIcon }),
        category: "other",
      },
      {
        label: t("fields.other.url"),
        tag: "url",
        hiddenFieldQueryKey: "url",
        type: "url",
        dataType: "URL",
        typeLabel: t("fields.other.url"),
        placeholder: t("fields.other.urlPlaceholder"),
        required: false,
        standard: false,
        custom: true,
        icon: () => [
          h(
            "div",
            {
              style: { display: "inline-block", padding: "0px !important" },
              class: "p-[0px]",
            },
            [newFieldBadge()]
          ),
          h(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              "stroke-width": "1.67",
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              class: "h-8 w-8 p-2",
            },
            [
              h("path", {
                d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71",
              }),
              h("path", {
                d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
              }),
            ]
          ),
        ],
        category: "other",
        urlValidation: { mode: "allow_all" },
      },
    ],
  },
};

/**
 * Pre-rollout "Date Picker" sidebar entry. Locations outside the
 * customFieldsPhase1 allowlist swap this in for the new "Date & time" entry:
 * legacy label, static placeholder, and no "New" badge on the icon.
 */
export const legacyDatePickerSidebarField = () => ({
  label: t("fields.other.datePickerLegacy"),
  tag: "date",
  hiddenFieldQueryKey: "date",
  type: "date",
  dataType: "DATE",
  typeLabel: t("fields.other.datePickerLegacy"),
  placeholder: t("fields.other.datePickerPlaceholder"),
  required: false,
  standard: false,
  custom: true,
  icon: () => h("img", { src: DatePickerIcon }),
  category: "other",
});

export const addressFields = standardFields.address.group.filter(
  (field) =>
    field.tag !== "group_address" &&
    field.tag !== "website" &&
    field.tag !== "organization"
);

export function getConstructedQueryKey(name) {
  return name.toLowerCase().trim().split(" ").join("_");
}

const LEGACY_DATE_TIME_GENERATED_NAME_BASES = [
  "Date",
  "Time",
  "Date & time",
  "Date Picker",
  "Date and time picker",
  "Time Picker",
  "Date and Time",
];
const GENERATED_FIELD_SUFFIX_PATTERN = /^\d+[a-z0-9]{3}$/;

type DateTimeGeneratedNameType = "DATE" | "TIME" | "DATE_TIME";

const resolveDateTimeGeneratedName = (
  value: string,
  key: string,
  fallback: string
): string => {
  return value && value !== key ? value : fallback;
};

export const getDateTimeGeneratedFieldNameBase = (
  dataType: string | undefined
): string | undefined => {
  switch (dataType) {
    case "DATE":
      return resolveDateTimeGeneratedName(
        t("fields.other.datePickerGeneratedName"),
        "fields.other.datePickerGeneratedName",
        "Date"
      );
    case "TIME":
      return resolveDateTimeGeneratedName(
        t("fields.other.timePickerGeneratedName"),
        "fields.other.timePickerGeneratedName",
        "Time"
      );
    case "DATE_TIME":
      return resolveDateTimeGeneratedName(
        t("fields.other.dateTimePickerGeneratedName"),
        "fields.other.dateTimePickerGeneratedName",
        "Date & time"
      );
    default:
      return undefined;
  }
};

export const getDateTimeGeneratedFieldName = (
  dataType: string | undefined,
  suffix: string
): string | undefined => {
  const nameBase = getDateTimeGeneratedFieldNameBase(dataType);
  return nameBase ? `${nameBase} ${suffix}` : undefined;
};

const getDateTimeGeneratedNameBases = (): string[] => {
  const translatedBases: DateTimeGeneratedNameType[] = [
    "DATE",
    "TIME",
    "DATE_TIME",
  ];
  const generatedNameBases = translatedBases
    .map((dataType) => getDateTimeGeneratedFieldNameBase(dataType))
    .filter((nameBase): nameBase is string => !!nameBase);
  return [
    ...new Set([
      ...generatedNameBases,
      ...LEGACY_DATE_TIME_GENERATED_NAME_BASES,
    ]),
  ].sort((first, second) => second.length - first.length);
};

export const getDateTimeGeneratedNameSuffix = (
  label: string | undefined
): string | undefined => {
  const trimmedLabel = label?.trim();
  if (!trimmedLabel) return undefined;
  const generatedNameBase = getDateTimeGeneratedNameBases().find((nameBase) =>
    trimmedLabel.startsWith(`${nameBase} `)
  );
  if (!generatedNameBase) return undefined;
  const suffix = trimmedLabel.slice(generatedNameBase.length + 1).trim();
  return GENERATED_FIELD_SUFFIX_PATTERN.test(suffix) ? suffix : undefined;
};

const NON_IMPORTABLE_FONTS = new Set([
  // CSS generic families
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-sans-serif",
  "ui-serif",
  "ui-monospace",
  "ui-rounded",
  // System/browser-installed fonts
  "arial",
  "helvetica",
  "times",
  "times new roman",
  "palatino",
  "palatino linotype",
  "garamond",
  "verdana",
  "georgia",
  "courier",
  "courier new",
  "trebuchet ms",
  "tahoma",
  "impact",
  "comic sans ms",
  "lucida console",
  "lucida sans unicode",
  "arial black",
]);

/**
 * @description Extracts unique font-family names from inline styles in an HTML string.
 * Skips generic CSS font families (serif, sans-serif, etc.) since they don't need loading.
 * @param html - Rich text HTML that may contain inline style="font-family: ..." attributes
 * @returns Array of unique, loadable font names found in the HTML
 */
export function getFontsFromRichTextHtml(html: string): string[] {
  if (!html || typeof html !== "string") {
    return [];
  }

  const fonts: string[] = [];
  const fontFamilyRegex = /font-family:\s*([^;}"]+)/gi;
  let match: RegExpExecArray | null;

  while ((match = fontFamilyRegex.exec(html)) !== null) {
    const rawValue = match[1].trim();
    const firstFont = rawValue
      .split(",")[0]
      .trim()
      .replace(/^['"]|['"]$/g, "");
    if (
      firstFont &&
      !NON_IMPORTABLE_FONTS.has(firstFont.toLowerCase()) &&
      !fonts.includes(firstFont)
    ) {
      fonts.push(firstFont);
    }
  }

  return fonts;
}

const RESULT_TEMPLATE_RTE_FIELDS: Record<string, string[][]> = {
  headerSettings: [["headingHtml"]],
  overallScoreSettings: [["content"], ["staticContent", "content"]],
  categoryScoreSettings: [["heading"], ["staticContent", "heading"]],
  ctaSettings: [["heading"], ["staticContent", "heading"]],
  footerSettings: [["footerHTML"]],
  individualCategorySettings: [["staticContent", "heading"]],
};

function collectHtmlStrings(obj: unknown): string[] {
  if (!obj || typeof obj !== "object") {
    return typeof obj === "string" ? [obj] : [];
  }
  const results: string[] = [];
  for (const val of Object.values(obj as Record<string, unknown>)) {
    if (typeof val === "string") {
      results.push(val);
    } else if (val && typeof val === "object") {
      results.push(...collectHtmlStrings(val));
    }
  }
  return results;
}

function extractFontsFromSectionConfig(
  config: Record<string, any>,
  sectionType: string
): string[] {
  const paths = RESULT_TEMPLATE_RTE_FIELDS[sectionType];
  if (!paths || !config) {
    return [];
  }
  const fonts: string[] = [];
  for (const path of paths) {
    let value: any = config;
    for (const key of path) {
      value = value?.[key];
    }
    if (value === undefined || value === null) {
      continue;
    }
    for (const html of collectHtmlStrings(value)) {
      for (const font of getFontsFromRichTextHtml(html)) {
        if (!fonts.includes(font)) {
          fonts.push(font);
        }
      }
    }
  }
  return fonts;
}

/**
 * @description Extracts unique font-family names from all RTE fields in a quiz resultTemplate.
 * Handles both the sections-based structure ({ sections: [{ type, config }] }) and
 * the flat structure (headerSettings, overallScoreSettings, etc.).
 * @param resultTemplate - The quiz result template object from the store
 * @returns Array of unique, loadable font names found across all RTE fields
 */
export function getFontsFromResultTemplate(
  resultTemplate: Record<string, any>
): string[] {
  if (!resultTemplate || typeof resultTemplate !== "object") {
    return [];
  }
  const fonts: string[] = [];
  const addFont = (font: string) => {
    if (font && !fonts.includes(font)) {
      fonts.push(font);
    }
  };

  if (
    Array.isArray(resultTemplate.sections) &&
    resultTemplate.sections.length > 0
  ) {
    for (const section of resultTemplate.sections) {
      if (section?.config && section?.type) {
        for (const font of extractFontsFromSectionConfig(
          section.config,
          section.type
        )) {
          addFont(font);
        }
      }
    }
  } else {
    for (const sectionType of Object.keys(RESULT_TEMPLATE_RTE_FIELDS)) {
      const config = resultTemplate[sectionType];
      if (config && typeof config === "object") {
        for (const font of extractFontsFromSectionConfig(config, sectionType)) {
          addFont(font);
        }
      }
    }
  }

  return fonts;
}

export function getAllFontsImport(fonts, isGDPRCompliant) {
  let fontParams = isGDPRCompliant
    ? "@import url('https://fonts.bunny.net/css?family="
    : "@import url('https://fonts.googleapis.com/css?family=";
  const fontLength = fonts.length;
  fonts.forEach((font: string, index) => {
    fontParams +=
      font +
      ":100,100i,300,300i,400,400i,500,500i,700,700i,900,900i" +
      (fontLength !== index + 1 ? "|" : "");
  });
  fontParams = fontParams + "');\n";
  return fontParams;
}

export function getAllUniqueFonts(
  fieldStyle,
  submitMessageStyle,
  elements,
  isForm,
  footerFont,
  thankyouText?: string,
  resultTemplate?: Record<string, any>
) {
  const finalFontCollection = [];
  if (
    fieldStyle.labelFontFamily &&
    !finalFontCollection.includes(fieldStyle.labelFontFamily)
  ) {
    finalFontCollection.push(fieldStyle.labelFontFamily);
  }
  if (
    fieldStyle.mobileLabelFontFamily &&
    !finalFontCollection.includes(fieldStyle.mobileLabelFontFamily)
  ) {
    finalFontCollection.push(fieldStyle.mobileLabelFontFamily);
  }
  if (
    fieldStyle.placeholderFontFamily &&
    !finalFontCollection.includes(fieldStyle.placeholderFontFamily)
  ) {
    finalFontCollection.push(fieldStyle.placeholderFontFamily);
  }
  if (
    fieldStyle.mobilePlaceholderFontFamily &&
    !finalFontCollection.includes(fieldStyle.mobilePlaceholderFontFamily)
  ) {
    finalFontCollection.push(fieldStyle.mobilePlaceholderFontFamily);
  }
  if (
    fieldStyle.shortLabel?.fontFamily &&
    !finalFontCollection.includes(fieldStyle.shortLabel.fontFamily)
  ) {
    finalFontCollection.push(fieldStyle.shortLabel.fontFamily);
  }
  if (
    fieldStyle.shortLabel?.mobileFontFamily &&
    !finalFontCollection.includes(fieldStyle.shortLabel.mobileFontFamily)
  ) {
    finalFontCollection.push(fieldStyle.shortLabel.mobileFontFamily);
  }
  if (
    submitMessageStyle.fontFamily &&
    !finalFontCollection.includes(submitMessageStyle.fontFamily)
  ) {
    finalFontCollection.push(submitMessageStyle.fontFamily);
  }

  const addFont = (font: string) => {
    if (font && !finalFontCollection.includes(font)) {
      finalFontCollection.push(font);
    }
  };

  const rteFonts: string[] = [];
  const addRteFont = (font: string) => {
    if (font && !rteFonts.includes(font)) {
      rteFonts.push(font);
    }
  };

  const addFontsFromElement = (field) => {
    if (field.fontFamily) {
      addFont(field.fontFamily);
    }
    if (field.tag === "header") {
      for (const font of getFontsFromRichTextHtml(field.label)) {
        addRteFont(font);
      }
    } else if (field.tag === "button") {
      for (const font of getFontsFromRichTextHtml(field.label)) {
        addRteFont(font);
      }
      if (field.submitSubText) {
        for (const font of getFontsFromRichTextHtml(field.submitSubText)) {
          addRteFont(font);
        }
      }
      if (field.subTextFontFamily) {
        addFont(field.subTextFontFamily);
      }
    } else if (field.tag === "terms_and_conditions") {
      const htmlFields = ["placeholder"];
      if (field.mobilePlaceholder) {
        htmlFields.push("mobilePlaceholder");
      }
      if (field.placeholder2) {
        htmlFields.push("placeholder2");
      }
      for (const htmlField of htmlFields) {
        if (field[htmlField] && typeof field[htmlField] === "string") {
          for (const font of getFontsFromRichTextHtml(field[htmlField])) {
            addRteFont(font);
          }
        }
      }
    }
  };

  if (isForm) {
    elements.forEach((field) => {
      if (
        field.tag === "header" ||
        field.tag === "button" ||
        field.tag === "terms_and_conditions"
      ) {
        addFontsFromElement(field);
      }
    });
  } else {
    elements.forEach((slide) => {
      slide.slideData.forEach((field) => {
        if (
          field.tag === "header" ||
          field.tag === "button" ||
          field.tag === "terms_and_conditions"
        ) {
          addFontsFromElement(field);
        }
      });
    });
    if (footerFont && !finalFontCollection.includes(footerFont)) {
      finalFontCollection.push(footerFont);
    }
  }

  if (thankyouText) {
    for (const font of getFontsFromRichTextHtml(thankyouText)) {
      addRteFont(font);
    }
  }

  if (resultTemplate) {
    for (const font of getFontsFromResultTemplate(resultTemplate)) {
      addRteFont(font);
    }
  }

  for (const font of rteFonts) {
    addFont(font);
  }

  return finalFontCollection;
}

export const convertSlideLogicToConditionalLogic = (slides, store) => {
  if (!slides || !slides.length) {
    return [];
  }

  const conditionalLogic = [];

  slides.forEach((slide) => {
    if (slide.logic) {
      for (const fieldId in slide.logic) {
        const fieldLogic = slide.logic[fieldId];

        let actualField = null;
        let hiddenFieldQueryKey = null;

        slides.forEach((s) => {
          if (s.slideData && Array.isArray(s.slideData)) {
            const field = s.slideData.find((item) => item.id === fieldId);
            if (field) {
              actualField = field;
              hiddenFieldQueryKey = field.hiddenFieldQueryKey;
            }
          }
        });

        if (!hiddenFieldQueryKey) {
          continue;
        }

        // Skip checkbox fields during migration
        if (
          actualField &&
          (actualField.type === "checkbox" ||
            actualField.dataType === "CHECKBOX" ||
            actualField.tag === "checkbox")
        ) {
          continue;
        }

        for (const optionKey in fieldLogic) {
          if (fieldLogic[optionKey] && fieldLogic[optionKey].status) {
            const option = fieldLogic[optionKey];

            let optionValue = option.optionName || "";

            if (!optionValue && actualField) {
              if (
                actualField.picklistOptions &&
                option.optionIndex < actualField.picklistOptions.length
              ) {
                optionValue = actualField.picklistOptions[option.optionIndex];
              } else if (actualField.picklistOptionsImage) {
                optionValue =
                  actualField.picklistOptionsImage[option.optionIndex]?.label ||
                  "";
              }
            }

            // Handle different logic types based on option.id
            let outcome;
            console.log("Form Action::", store?.formAction);
            if (option.id === "disqualify") {
              // Disqualify immediately
              outcome = {
                type: "disqualifyLead",
                value: store?.formAction?.disqualifiedText || "",
                disqualifyAction: "showCustomMessage",
                disqualifyTiming: "disqualifyImmediately",
              };
            } else if (option.id === "endsurvey") {
              // Disqualify after submit
              outcome = {
                type: "disqualifyLead",
                value: store?.formAction?.endsurveyText || "",
                disqualifyAction: "showCustomMessage",
                disqualifyTiming: "disqualifyAfterSubmit",
              };
            } else {
              // Regular jump to logic (slide ID)
              outcome = {
                type: "jumpTo",
                value: "",
                jumpTarget: option.id,
              };
            }

            conditionalLogic.push({
              conditions: [
                {
                  selectedField: hiddenFieldQueryKey,
                  selectedOperation: "isEqualTo",
                  inputValue: optionValue,
                },
              ],
              outcome,
              conditionalOperation: "then",
            });
          }
        }
      }

      slide.logic = {};
    }
  });

  return conditionalLogic;
};

// Migrates slide-level jumpTo settings into conditionalLogic rules
// For each slide that has a jumpTo target, creates or updates a corresponding
// Jump To rule with a slide-based IF condition.
export const convertSlideJumpToToConditionalLogic = (
  slides: any[] = [],
  existingRules: any[] = []
) => {
  const rules = Array.isArray(existingRules) ? [...existingRules] : [];

  if (!Array.isArray(slides) || !slides.length) {
    return rules;
  }

  slides.forEach((slide) => {
    const originId = slide?.id;
    const target = slide?.jumpTo;
    if (!originId || !target) {
      return;
    }

    // Find an existing simple rule for this slide (single IF based on slide)
    const existingIdx = rules.findIndex(
      (r) =>
        r?.outcome?.type === "jumpTo" &&
        Array.isArray(r?.conditions) &&
        r.conditions.length === 1 &&
        r.conditions[0]?.selectedField === originId
    );

    const jumpRule = {
      conditions: [
        {
          selectedField: originId,
          selectedOperation: "isEqualTo",
          inputValue: null,
          fieldMode: "slide",
        },
      ],
      outcome: {
        type: "jumpTo",
        value: "",
        jumpTarget: target,
        jumpTargetType: "slide",
      },
      conditionalOperation: "then",
    } as any;

    if (existingIdx >= 0) {
      // Update outcome target while preserving rule position and any metadata
      rules[existingIdx].outcome = jumpRule.outcome;
    } else {
      rules.push(jumpRule);
    }
  });

  return rules;
};

export const applyStyles = (
  html,
  linkColor = DEFAULT_LABEL_COLOR,
  textColor = DEFAULT_TEXT_COLOR
) => {
  const styledHTML = html
    ? html
        .replace(
          /<a /g,
          `<a style="color: #${linkColor}; text-decoration: none;" `
        )
        .replace(
          /\|/g,
          `<br><input type="checkbox" style="position: absolute; left: 0;">`
        )
    : html;
  return `<span style="color: #${textColor};">${styledHTML}</span>`;
};

export const generateFooterStyle = (store, forceDeviceMode?: string) => {
  const isMobile = forceDeviceMode
    ? forceDeviceMode === "mobile"
    : store.deviceMode === "mobile";
  const footerStyle = isMobile ? store?.mobileFooterStyle : store?.footerStyle;
  const footerBorderRadius = `${store.formStyle?.border?.radius}px`;
  const style = store?.formStyle;
  const stickyShadow =
    style?.shadow?.horizontal +
    "px " +
    style?.shadow?.vertical +
    "px " +
    style?.shadow?.blur +
    "px " +
    style?.shadow?.spread +
    "px #" +
    style?.shadow?.color;

  const progressBarStyle = footerStyle.progressBarStyle;
  const buttonStyle = footerStyle?.buttonStyle;

  const containerStyle = {
    color: `#${buttonStyle.fontColor}`,
    justifyContent: isMobile ? "space-between" : "flex-end",
    position: footerStyle?.stickyFooter ? "static" : "absolute",
    height: `${footerStyle?.height}px`,
  };

  // Position is only available in desktop mode
  if (!isMobile && footerStyle?.buttonStyle?.position) {
    switch (footerStyle.buttonStyle.position) {
      case RIGHT:
        containerStyle.justifyContent = "flex-end";
        break;
      case LEFT:
        containerStyle.justifyContent = "flex-start";
        break;
      case LEFT_AND_RIGHT:
        containerStyle.justifyContent = "space-between";
        break;
      default:
        containerStyle.justifyContent = "flex-end";
    }
  }

  const loaderStyle: any = {
    transform: "translate(-50%, -50%)",
  };
  if (footerStyle.stickyFooter) {
    loaderStyle.transform = "translate(-50%, 20%)";
    loaderStyle.position = "relative";
    if (!isMobile && footerStyle?.buttonStyle?.position === LEFT) {
      loaderStyle.left = "38%";
    } else {
      loaderStyle.right = "30px";
    }
  } else {
    loaderStyle.position = "absolute";
    loaderStyle.top = "50%";
    if (!isMobile && footerStyle?.buttonStyle?.position === LEFT) {
      loaderStyle.left = "15%";
    } else {
      loaderStyle.right = "35px";
    }
  }
  const formStyle = store?.formStyle;
  const boxShadow = `${formStyle?.shadow?.horizontal}px ${formStyle?.shadow?.vertical}px ${formStyle?.shadow?.blur}px ${formStyle?.shadow?.spread}px #${formStyle?.shadow?.color}`;
  const borderRadius = `${formStyle?.border?.radius}px`;

  const styles = {
    footer: {
      position: footerStyle.stickyFooter ? "static" : "fixed",
      backgroundColor: `#${footerStyle.backgroundFill}`,
      height: `${footerStyle.height}px`,
      fontSize: `${footerStyle.fontSize}px`,
      fontWeight: footerStyle.fontWeight,
      fontFamily: footerStyle.fontFamily,
      boxShadow: footerStyle.stickyFooter
        ? stickyShadow
        : DEFAULT_FOOTER_SHADOW,
      webkitBoxShadow: footerStyle.stickyFooter
        ? stickyShadow
        : DEFAULT_FOOTER_SHADOW,
      borderRadius: footerStyle.stickyFooter
        ? `0 0 ${footerBorderRadius} ${footerBorderRadius}`
        : 0,
    },
    progressBar: {
      barStyle: {
        border: isMobile
          ? "none"
          : `${progressBarStyle?.borderWidth}px solid #${progressBarStyle?.borderColor}`,
        borderRadius: isMobile ? "8px" : `${progressBarStyle?.borderRadius}px`,
        backgroundColor: `#${progressBarStyle.inactiveFillColor}`,
      },
      fillStyle: {
        backgroundColor: `#${progressBarStyle.completeFillColor}`,
        color: `#${progressBarStyle.textColor}`,
        borderRadius: isMobile
          ? "8px 0 0 8px"
          : progressBarStyle?.borderRadius < 16
          ? `${progressBarStyle?.borderRadius - 4}px 0 0 ${
              progressBarStyle?.borderRadius - 4
            }px`
          : `${progressBarStyle?.borderRadius}px 0 0 ${progressBarStyle?.borderRadius}px`,
      },
      textStyle: { color: `#${progressBarStyle.textColor}` },
    },
    container: containerStyle,
    nextButton: {
      backgroundColor: isMobile
        ? "transparent"
        : `#${buttonStyle?.nextButtonBgColor}`,
      height: `${footerStyle.height}px`,
      borderRadius:
        footerStyle?.stickyFooter &&
        [RIGHT, LEFT_AND_RIGHT].includes(footerStyle?.buttonStyle?.position)
          ? `0 0 ${store.formStyle?.border?.radius}px 0`
          : 0,
    },
    submitButton: {
      backgroundColor: `#${buttonStyle.submitButtonBgColor}`,
      height: `${footerStyle.height}px`,
      borderRadius:
        footerStyle?.stickyFooter &&
        [RIGHT, LEFT_AND_RIGHT].includes(
          isMobile ? "leftAndRight" : footerStyle?.buttonStyle?.position
        )
          ? `0 0 ${store.formStyle?.border?.radius}px 0`
          : 0,
    },
    backButton: {
      backgroundColor: isMobile
        ? "transparent"
        : `#${buttonStyle?.backButtonBgColor}`,
      height: `${footerStyle.height}px`,
      opacity: 0.8,
      borderRadius:
        footerStyle?.stickyFooter &&
        [LEFT, LEFT_AND_RIGHT].includes(
          isMobile ? "leftAndRight" : footerStyle?.buttonStyle?.position
        )
          ? `0 0 0 ${store.formStyle?.border?.radius}px`
          : 0,
    },
    icon: {
      verticalAlign: "text-bottom",
      width: isMobile ? "20px" : `${footerStyle.fontSize * 1.2}px`,
      height: isMobile ? "20px" : `${footerStyle.fontSize * 1.2}px`,
      color: `#${buttonStyle.fontColor}`,
    },
    arrowColor: `#${buttonStyle.fontColor}`,
    loader: loaderStyle,
    surveyWrapper: {
      boxShadow: footerStyle?.stickyFooter ? boxShadow : 0,
      borderRadius: footerStyle?.stickyFooter ? borderRadius : 0,
      marginBottom: `${footerStyle?.height || 60}px`,
    },
  };

  // Mobile always shows arrow only, desktop uses the configured button type
  const showArrow = isMobile
    ? true
    : [ONLY_ARROW, TEXT_AND_ARROW].includes(
        footerStyle?.buttonStyle?.buttonType
      );
  const showText = isMobile
    ? false
    : [ONLY_TEXT, TEXT_AND_ARROW].includes(
        footerStyle?.buttonStyle?.buttonType
      );

  return {
    styles,
    showArrow,
    showText,
  };
};

export const allOperationOptions = [
  {
    label: t("form.conditonalLogic.allOperationOptions.isEqualTo"),
    value: "isEqualTo",
    types: [
      "text",
      "large_text",
      "phone",
      "email",
      "single_options",
      "select",
      "radio",
      "multiple_options",
      "checkbox",
      "monetory",
      "date",
      "time",
      "date_time",
      "numerical",
      "score",
      "rating",
    ],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.isNotEqualTo"),
    value: "isNotEqualTo",
    types: [
      "text",
      "large_text",
      "phone",
      "email",
      "single_options",
      "select",
      "radio",
      "multiple_options",
      "checkbox",
      "monetory",
      "date",
      "time",
      "date_time",
      "numerical",
      "score",
      "rating",
    ],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.contains"),
    value: "contains",
    types: ["text", "large_text", "phone", "email"],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.doesNotContain"),
    value: "doesNotContain",
    types: ["text", "large_text", "phone", "email"],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.startsWith"),
    value: "startsWith",
    types: ["text", "large_text", "phone", "email"],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.endsWith"),
    value: "endsWith",
    types: ["text", "large_text", "phone", "email"],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.isEmpty"),
    value: "isEmpty",
    types: [
      "text",
      "large_text",
      "phone",
      "email",
      "date",
      "time",
      "date_time",
      "single_options",
      "select",
      "radio",
      "multiple_options",
      "checkbox",
      "numerical",
      "monetory",
      "score",
      "rating",
      "file_upload",
      "signature",
    ],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.isFilled"),
    value: "isFilled",
    types: [
      "text",
      "large_text",
      "phone",
      "email",
      "date",
      "time",
      "date_time",
      "single_options",
      "select",
      "radio",
      "multiple_options",
      "checkbox",
      "numerical",
      "monetory",
      "score",
      "rating",
      "file_upload",
      "signature",
    ],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.before"),
    value: "before",
    types: ["date", "time", "date_time"],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.after"),
    value: "after",
    types: ["date", "time", "date_time"],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.greaterThan"),
    value: "greaterThan",
    types: ["numerical", "monetory", "score", "rating"],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.lessThan"),
    value: "lessThan",
    types: ["numerical", "monetory", "score", "rating"],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.between"),
    value: "between",
    types: [
      "numerical",
      "monetory",
      "score",
      "rating",
      "date",
      "time",
      "date_time",
    ],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.isChecked"),
    value: "isChecked",
    types: ["terms_and_conditions"],
  },
  {
    label: t("form.conditonalLogic.allOperationOptions.isNotChecked"),
    value: "isNotChecked",
    types: ["terms_and_conditions"],
  },
];

export const camelToPascal = (str) => {
  return str?.split(/(?=[A-Z])/)?.join(" ");
};

export const isPaymentGatewayConnected = (paymentIntegration, isLive) => {
  if (paymentIntegration == null || typeof paymentIntegration !== "object") {
    return false;
  }
  let isConnected = false;
  console.log(paymentIntegration, "paymentIntegration");
  Object.entries(paymentIntegration).forEach(([key, value]: [string, any]) => {
    if (validateAllPaymentIntegration(key, value, isLive)) {
      isConnected = true;
      return true;
    }
  });
  return isConnected;
};

const validateAllPaymentIntegration = (gateway, paymentObj, isLive) => {
  if (gateway === "stripe" && isLive && paymentObj.live.publishableKey) {
    return true;
  }
  if (gateway === "stripe" && !isLive && paymentObj.test.publishableKey) {
    return true;
  }
  if (gateway === "nmi" && isLive && paymentObj.live.merchantPublicKey) {
    return true;
  }
  if (gateway === "nmi" && !isLive && paymentObj.test.merchantPublicKey) {
    return true;
  }
  if (
    gateway === "authorizeNet" &&
    isLive &&
    paymentObj.live.merchantPublicClientKey
  ) {
    return true;
  }
  if (
    gateway === "authorizeNet" &&
    !isLive &&
    paymentObj.test.merchantPublicClientKey
  ) {
    return true;
  }
  if (gateway === "square" && isLive && paymentObj.live.merchantGatewayId) {
    return true;
  }
  if (gateway === "square" && !isLive && paymentObj.test.merchantGatewayId) {
    return true;
  }
  if (gateway === "paypal" && isLive && paymentObj.live.paypalPublishableKey) {
    return true;
  }
  if (gateway === "paypal" && !isLive && paymentObj.test.paypalPublishableKey) {
    return true;
  }
  if (gateway === "adyen" && isLive && paymentObj.live.clientKey) {
    return true;
  }
  if (gateway === "adyen" && !isLive && paymentObj.test.clientKey) {
    return true;
  }
};

export const convertToNumbers = (arrayOfNumberLikeStrings) => {
  const numbers = [];
  arrayOfNumberLikeStrings.forEach((number) => {
    const parsedNumber = parseFloat(parseFloat(number).toFixed(2));
    if (parsedNumber && parsedNumber > 0) {
      return numbers.push(parsedNumber);
    }
  });
  return numbers;
};

export const convertToStrings = (arrayOfNumbers) => {
  return arrayOfNumbers.map((number) => {
    return number.toString();
  });
};

export const showFieldLabel = (formLabelVisible, item: any) => {
  if (
    formLabelVisible &&
    (item.type === "text" ||
      item.type === "email" ||
      item.type === "captcha" ||
      item.type === "date" ||
      item.type === "select" ||
      item.type === "radio" ||
      item.type === "payment")
  ) {
    return true;
  }
  return false;
};

export const dateFormatForUI = (format = "YYYY-MM-DD", separator = "-") => {
  return (format.replace(/-/g, separator) || "YYYY-MM-DD")
    .toLowerCase()
    .replace(/mm/g, "MM");
};

export const dateFormatForDayjs = (format = "YYYY-MM-DD", separator = "-") => {
  return formatDatePreview(format, separator);
};

// =============================
// DOM helpers for label focus
// =============================

const logError = (context: string, error: unknown) => {
  console.warn(`[focus-helper] ${context}`, error);
};

const focusOptionLabel = (el: HTMLElement) => {
  try {
    el.focus?.({ preventScroll: true });
    if (!el.isContentEditable) {
      selectAllText(el);
      return;
    }
    const rawText = (el.textContent || "").replace(/\u200B/g, "");
    let textNode = el.firstChild as Text | null;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
      el.textContent = "";
      textNode = el.ownerDocument.createTextNode(rawText);
      el.appendChild(textNode);
    }
    if (!rawText) {
      if (!textNode.textContent) {
        textNode.textContent = "\u200B";
      }
      const range = el.ownerDocument.createRange();
      range.setStart(textNode, 0);
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
    selectAllText(el);
  } catch (error) {
    logError("Failed to focus option label", error);
  }
};

const findInlineLabel = (root?: HTMLElement | null): HTMLElement | null => {
  const resolveInScope = (scope?: HTMLElement | null): HTMLElement | null => {
    if (!scope) {
      return null;
    }
    const street = scope.querySelector(
      '[data-address-tag="address"].label-input[contenteditable="true"]'
    ) as HTMLElement | null;
    if (isElementVisible(street)) {
      return street;
    }
    const generic = scope.querySelector(
      ".label-input[contenteditable='true']"
    ) as HTMLElement | null;
    return isElementVisible(generic) ? generic : null;
  };

  const scoped = resolveInScope(root);
  if (scoped) {
    return scoped;
  }

  const active = document.querySelector(
    ".form-builder--item[data-active='true']"
  ) as HTMLElement | null;
  return resolveInScope(active);
};

/**
 * Focus and select the label text
 */
export const focusLabelFromUserEvent = (e?: Event) => {
  try {
    const current = (e?.currentTarget as HTMLElement) || null;
    const target = (e?.target as HTMLElement) || null;
    // If the user clicked inside another editable region (not the main label), don't steal focus
    const clickedEditable = target?.closest(
      '[contenteditable="true"]'
    ) as HTMLElement | null;
    if (clickedEditable?.classList.contains("label-input") === false) {
      return;
    }
    // Also skip when clicking on standard inputs within the element
    if (target?.closest("input, textarea, select, button")) {
      return;
    }
    let wrapper =
      current ||
      (target?.closest(
        ".smooth-dnd-draggable-wrapper.element-box.menu-field-wrap.form-field-wrapper"
      ) as HTMLElement | null);
    if (!wrapper && target) {
      wrapper = target.closest(
        ".smooth-dnd-draggable-wrapper.element-box.menu-field-wrap"
      ) as HTMLElement | null;
    }
    const inline = findInlineLabel(wrapper);
    if (inline) {
      inline.focus?.({ preventScroll: true });
      selectAllText(inline);
    }
  } catch (error) {
    logError("Failed to focus label from user click event", error);
  }
};

/**
 * Focus label after selection changes programmatically.
 * Uses a small timeout to allow DOM overlays to settle.
 */
export const focusLabelAfterActiveChange = () => {
  nextTick(() => {
    // If user already focused an editable/input within the element, don't override
    const activeEl = document.activeElement as HTMLElement | null;
    const activeEditable = activeEl?.closest(
      '[contenteditable="true"]'
    ) as HTMLElement | null;
    if (activeEditable?.classList.contains("label-input") === false) {
      return;
    }
    if (activeEl?.closest("input, textarea, select, button")) {
      return;
    }
    const inline = findInlineLabel();
    if (inline) {
      try {
        inline.focus?.({ preventScroll: true });
        selectAllText(inline);
      } catch (error) {
        logError(
          "Failed to focus and select label after element activation",
          error
        );
      }
    }
  });
};

/**
 * Handles Tab/Shift+Tab navigation across editable option labels.
 * Calls getElByIndex with the computed target index and focuses + selects it.
 */
export const handleOptionTabNavigation = (
  event: KeyboardEvent,
  index: number,
  total: number,
  getElByIndex: (i: number) => HTMLElement | null
): void => {
  // Only handle actual Tab key presses
  if (event.key !== "Tab") {
    return;
  }
  const delta = event.shiftKey ? -1 : 1;
  const targetIndex = index + delta;

  // Attempt to find the next element via provided resolver
  const resolveAndFocus = (idx: number): boolean => {
    const el = getElByIndex(idx);
    if (el) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof (event as any).stopImmediatePropagation === "function") {
        (event as any).stopImmediatePropagation();
      }
      // Ensure current element won't re-handle keyup
      const current = event.target as HTMLElement | null;
      try {
        current?.blur?.();
      } catch (error) {
        logError("Failed to blur current element during tab navigation", error);
      }
      nextTick(() => {
        try {
          focusOptionLabel(el);
        } catch (error) {
          logError(
            "Failed to focus target option label after tab navigation",
            error
          );
        }
      });
      return true;
    }
    return false;
  };

  // Try resolver path first when index looks valid
  if (targetIndex >= 0 && targetIndex < total) {
    if (resolveAndFocus(targetIndex)) {
      return;
    }
  }

  // Derive neighbors from DOM within the same options list
  const current = (event.target as HTMLElement) || null;
  const container = current?.closest(".w-full");
  if (!container) {
    return;
  }
  const labels = Array.from(
    container.querySelectorAll('label[contenteditable="true"]')
  ) as HTMLElement[];
  if (!labels.length) {
    return;
  }
  const pos = labels.indexOf(current as HTMLElement);
  if (pos === -1) {
    return;
  }
  const domTarget = pos + delta;
  if (domTarget < 0 || domTarget >= labels.length) {
    return; // allow native tab when we can't navigate
  }
  event.preventDefault();
  event.stopPropagation();
  if (typeof (event as any).stopImmediatePropagation === "function") {
    (event as any).stopImmediatePropagation();
  }
  // Blur current to avoid its keyup listeners fighting focus
  try {
    (current as HTMLElement | null)?.blur?.();
  } catch (error) {
    logError(
      "Failed to blur element during DOM fallback tab navigation",
      error
    );
  }
  nextTick(() => {
    const el = labels[domTarget];
    try {
      focusOptionLabel(el);
    } catch (error) {
      logError(
        "Failed to focus next option via DOM fallback during tab navigation",
        error
      );
    }
  });
};

/**
 * From a field label, move focus to the first editable option on Tab.
 * Returns true if it handled focus, false to allow native behavior.
 */
export const handleLabelTabNavigation = (event: KeyboardEvent): boolean => {
  if (event.key !== "Tab" || event.shiftKey) {
    return false; // Only handle forward Tab
  }

  const current = (event.target as HTMLElement) || null;
  if (!current) {
    return false;
  }
  const wrapper = current.closest(".form-builder--item") as HTMLElement | null;
  if (!wrapper) {
    return false;
  }
  // Option labels are real <label contenteditable="true"> elements across components
  const firstOption = wrapper.querySelector(
    'label[contenteditable="true"]'
  ) as HTMLElement | null;
  if (!isElementVisible(firstOption)) {
    return false;
  }
  try {
    event.preventDefault();
    event.stopPropagation();
    if (typeof (event as any).stopImmediatePropagation === "function") {
      (event as any).stopImmediatePropagation();
    }
    // Blur current so it doesn't re-handle keyup
    try {
      (current as HTMLElement | null)?.blur?.();
    } catch (error) {
      logError("Failed to blur field label during tab to options", error);
    }
    nextTick(() => {
      try {
        focusOptionLabel(firstOption!);
      } catch (error) {
        logError(
          "Failed to focus first option label after tabbing from field label",
          error
        );
      }
    });
    return true;
  } catch (error) {
    logError("Tab navigation from label to options failed unexpectedly", error);
    return false;
  }
};

/**
 * Suppress Tab keyup to avoid previous element re-handling the event.
 */
export const suppressTabKeyUp = (event: KeyboardEvent) => {
  if (event.key === "Tab") {
    event.preventDefault();
    event.stopPropagation();
    if (typeof (event as any).stopImmediatePropagation === "function") {
      (event as any).stopImmediatePropagation();
    }
  }
};

/**
 * Compute whether a builder element is currently active across form, survey, or quiz contexts.
 */
export const isBuilderElementActive = (
  store: any,
  params: {
    elementIndex: number;
    slideIndex?: number;
    elementId?: string;
    slideId?: string | number;
  }
): boolean => {
  // Form builder context (no survey/quiz) or explicit formId
  if (store?.formId || (!store?.surveyId && !store?.quizId)) {
    return store?.currentCustomSidebarPreview === params.elementIndex;
  }
  // Quiz context uses slide id + element uuid
  if (store?.quizId) {
    return (
      store?.currentActiveIndex?.[0] ===
        (params.slideId ?? params.slideIndex) &&
      store?.currentActiveIndex?.[1] ===
        (params.elementId ?? params.elementIndex)
    );
  }
  // Survey context uses slide index + element index
  return (
    store?.currentActiveIndex?.[0] === params.slideIndex &&
    store?.currentActiveIndex?.[1] === params.elementIndex
  );
};

export const fieldFullWidth = (type: string, isLayoutSingleLine = false) => {
  if (isLayoutSingleLine && type === "submit") {
    return false;
  }
  if (FULL_WIDTH_FIELDS.indexOf(type) >= 0) {
    return true;
  }
  return false;
};

export const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const openLinkInNewTab = (url) => {
  const win = window.open(url, "_blank");
  win.focus();
};

export const configureRatingElement = (elementDrop: any) => {
  if (elementDrop.type === "rating" || elementDrop.dataType === "RATING") {
    return {
      ...elementDrop,
      ...RATING_ELEMENT_DEFAULTS, // This adds all default rating properties
      dataType: "RATING",
      type: "rating",
      typeLabel: "Rating",
    };
  }
  return elementDrop;
};

// Drop-only conversion helper for custom object TEXTBOX_LIST
export const convertTextboxListOptionsForCustomObjectOnDrop = (
  element: any,
  isCustomObject: boolean
) => {
  try {
    if (element?.dataType === "TEXTBOX_LIST" && isCustomObject) {
      const src = Array.isArray(element?.options) ? element.options : [];
      if (src.length) {
        const textBoxListOptions = src.map((o: any, idx: number) => {
          const keyAsId = (o?.key ?? o?.label ?? `${idx}`).toString();
          return {
            id: keyAsId,
            label: (o?.label ?? o?.key ?? "").toString(),
            prefillValue: "",
          };
        });
        element.textBoxListOptions = textBoxListOptions;
        element.picklistOptions = textBoxListOptions;
      }
    } else if (
      isCustomObject &&
      ["SINGLE_OPTIONS", "MULTIPLE_OPTIONS", "CHECKBOX", "RADIO"].includes(
        element?.dataType
      )
    ) {
      // For custom object choice fields, derive options from backend-provided options
      const src = Array.isArray(element?.options) ? element.options : [];
      if (src.length) {
        if (element?.dataType === "RADIO") {
          // RADIO with image: prefer picklistOptionsImage only
          const imageOptions = src
            .filter(
              (o: any) =>
                typeof o === "object" && (o?.url || o?.image || o?.img)
            )
            .map((o: any) => ({
              label: (o?.label ?? o?.key ?? "").toString(),
              src: (o?.url ?? o?.image ?? o?.img ?? "").toString(),
            }));
          if (imageOptions.length) {
            element.picklistOptionsImage = imageOptions;
            // Remove picklistOptions when using image variant
            delete (element as any).picklistOptions;
          } else {
            // Fallback: no images, use labels list
            const labels = src.map((o: any) =>
              typeof o === "string" ? o : (o?.label ?? o?.key ?? "").toString()
            );
            element.picklistOptions = labels;
          }
        } else {
          // Non-RADIO choice fields: use labels list
          const labels = src.map((o: any) =>
            typeof o === "string" ? o : (o?.label ?? o?.key ?? "").toString()
          );
          element.picklistOptions = labels;
        }
      }
    } else if (isCustomObject && element?.dataType === "FILE_UPLOAD") {
      // For custom object file upload, mirror accepted formats into picklistOptions on drop
      const fmts = Array.isArray(element?.acceptedFormats)
        ? element.acceptedFormats
        : Array.isArray((element as any)?.acceptedFormat)
        ? (element as any).acceptedFormat
        : Array.isArray(element?.options)
        ? (element.options as any[]).filter((v) => typeof v === "string")
        : [];
      element.picklistOptions = fmts;
    }
  } catch (error) {
    logError(
      "Failed to convert textbox list options for custom object on drop",
      error
    );
  }
  return element;
};
export const updateOptionsWithFocusPreservation = (
  newOptions: any[],
  currentOptions: any[],
  defaultOption: any,
  activeInputIdRef: any,
  activeInputPositionRef: any
) => {
  if (!newOptions || !Array.isArray(newOptions)) {
    newOptions = [defaultOption];
  }

  const activeElement = document.activeElement as HTMLInputElement;
  if (activeElement?.id) {
    activeInputIdRef.value = activeElement.id;
    activeInputPositionRef.value = activeElement.selectionStart;
  }

  const processedOptions =
    Array.isArray(newOptions) && typeof newOptions[0] === "string"
      ? newOptions.map((option) => ({ label: option, calculatedValue: "" }))
      : [...newOptions];

  if (activeInputIdRef.value) {
    nextTick(() => {
      try {
        const element = document.getElementById(
          activeInputIdRef.value
        ) as HTMLInputElement;
        if (element) {
          element.focus();
          if (activeInputPositionRef.value !== null) {
            element.selectionStart = activeInputPositionRef.value;
            element.selectionEnd = activeInputPositionRef.value;
          }
        }
      } catch (error) {
        logError(
          "Failed to restore focus and cursor position after options update",
          error
        );
      }
    });
  }

  return processedOptions;
};

export const getUniqueId = () => {
  return Math.random().toString(36).slice(2, 11);
};

export const getElementDOMUniqueId = (
  prefix: string,
  element,
  index: number,
  slideIndex = 0
) => {
  return `${prefix}-${
    element?.hiddenFieldQueryKey || element?.fieldKey || index
  }-${slideIndex}-${getUniqueId()}`;
};

/**
 * Normalize a dropped contact STANDARD_FIELD (coming from Custom Fields sidebar)
 * to match built-in standard field definitions (Quick Add shape).
 * Mutates and returns the provided element object.
 */
export const normalizeContactStandardFieldDrop = (elementDrop: any): any => {
  if (
    elementDrop?.model === "contact" &&
    (elementDrop?.dataType === "STANDARD_FIELD" ||
      elementDrop?.type === "standard_field" ||
      elementDrop?.standard === true)
  ) {
    try {
      const suffix =
        (elementDrop?.fieldKey || "").toString().split(".").pop() || "";
      const personalDefs = (standardFields as any)?.personal?.group || [];
      // Include address group (exclude the parent group item)
      const addressDefs = (
        (standardFields as any)?.address?.group || []
      ).filter((d: any) => d?.tag !== "group_address");
      const candidateDefs = [...personalDefs, ...addressDefs];
      const stdDef = candidateDefs.find(
        (def: any) => def?.tag === suffix || def?.hiddenFieldQueryKey === suffix
      );
      if (stdDef) {
        elementDrop.tag = stdDef.tag;
        elementDrop.type = stdDef.type;
        elementDrop.typeLabel =
          (stdDef as any).typeLabel ?? elementDrop.typeLabel;
        elementDrop.hiddenFieldQueryKey =
          (stdDef as any).hiddenFieldQueryKey || stdDef.tag;
        elementDrop.label =
          elementDrop.name || stdDef.label || elementDrop.label;
        elementDrop.title = (stdDef as any).title ?? elementDrop.title;
        elementDrop.placeholder =
          (stdDef as any).placeholder ||
          elementDrop.placeholder ||
          elementDrop.label;
        elementDrop.required = stdDef.required ?? elementDrop.required ?? false;
        if ((stdDef as any).format) {
          (elementDrop as any).format = (stdDef as any).format;
        }
        if ((stdDef as any).separator) {
          (elementDrop as any).separator = (stdDef as any).separator;
        }
        if (typeof (stdDef as any).enableCountryPicker === "boolean") {
          (elementDrop as any).enableCountryPicker = (
            stdDef as any
          ).enableCountryPicker;
        }
        // Ensure standard contact field shape
        elementDrop.standard = true;
        elementDrop.custom = false;

        // Remove sidebar/DB only metadata to match quick-add shape
        delete (elementDrop as any).id;
        delete (elementDrop as any).Id;
        delete (elementDrop as any)._id;
        delete (elementDrop as any).position;
        delete (elementDrop as any).documentType;
        delete (elementDrop as any).fieldsCount;
        delete (elementDrop as any).picklistOptionsImage;
        delete (elementDrop as any).parentId;
        delete (elementDrop as any).locationId;
        delete (elementDrop as any).dateAdded;
        delete (elementDrop as any).dateUpdated;
        delete (elementDrop as any).dataType; // remove STANDARD_FIELD marker
        delete (elementDrop as any).name; // name not needed post-normalization
        // For contact fields, drop model to avoid custom-object logic paths
        delete (elementDrop as any).model;
      }
    } catch (error) {
      logError(
        "Failed to normalize contact standard field during drop operation",
        error
      );
    }
  }
  return elementDrop;
};

/**
 * For business (company) standard fields dropped from sidebars, prefix the
 * query key with company_ to keep consistent hiddenFieldQueryKey.
 * Mutates and returns the provided element object.
 */
export const applyBusinessStandardFieldPrefix = (elementDrop: any): any => {
  if (
    elementDrop?.model === "business" &&
    (elementDrop?.dataType === "STANDARD_FIELD" ||
      elementDrop?.type === "standard_field" ||
      elementDrop?.standard === true)
  ) {
    const key = elementDrop.hiddenFieldQueryKey || elementDrop.tag || "";
    if (key && !key.startsWith("company_")) {
      elementDrop.hiddenFieldQueryKey = `company_${key}`;
    }
  }
  return elementDrop;
};

/**
 * Returns a unique hiddenFieldQueryKey by appending `_copy` until not present in existingKeys
 */
export const getUniqueCopyHiddenKey = (
  originalKey: string,
  existingKeys: string[]
): string => {
  let candidate = `${originalKey}_copy`;
  while (existingKeys.includes(candidate)) {
    candidate = `${candidate}_copy`;
  }
  return candidate;
};
/**
 * Refreshes the trailing 3-character base36 code in labels created at add-time.
 * Detects patterns like: "... <digits><3-char>" (optionally followed by " (copy)")
 * and replaces only the last 3 characters with a new random base36 code.
 */
export const refreshLabelThreeCharCode = (label: string): string => {
  if (!label || typeof label !== "string") {
    return label;
  }
  // Preserve a trailing (copy) suffix while matching the numeric+3-char pattern
  const copySuffixMatch = label.match(/\s*\(copy\)+$/i);
  const suffix = copySuffixMatch ? copySuffixMatch[0] : "";
  const base = suffix ? label.slice(0, label.length - suffix.length) : label;
  const newCode = Math.random().toString(36).substr(2, 3);
  // Only replace when pattern ends with digits followed by a 3-char base36 code
  if (/(\d+)[a-z0-9]{3}$/i.test(base)) {
    const updated = base.replace(/(\d+)[a-z0-9]{3}$/i, `$1${newCode}`);
    return updated + suffix;
  }
  // Otherwise, do not change (avoid touching labels ending with 3 letters)
  return label;
};
/**
 * Returns a unique name by appending " (copy)" repeatedly until unique among existingNames
 */
export const getUniqueCopyName = (
  originalName: string,
  existingNames: string[]
): string => {
  let candidate = `${originalName} (copy)`;
  while (existingNames.includes(candidate)) {
    candidate = `${candidate} (copy)`;
  }
  return candidate;
};
/**
 * Deep clones an element and ensures its hiddenFieldQueryKey is unique by appending `_copy` repeatedly.
 * Optionally ensures a fresh uuid.
 */
export const cloneElementWithUniqueHiddenKey = (
  element: any,
  existingKeys: string[],
  options?: { ensureUuid?: boolean }
): any => {
  const cloned = cloneDeep(element);
  if (cloned && typeof cloned.hiddenFieldQueryKey === "string") {
    cloned.hiddenFieldQueryKey = getUniqueCopyHiddenKey(
      cloned.hiddenFieldQueryKey,
      existingKeys
    );
  }
  if (options?.ensureUuid && cloned && cloned.uuid) {
    cloned.uuid = uuidv4();
  }
  return cloned;
};

/**
 * Builds payload and key metadata for cloning a custom field on server.
 */
export const buildClonedCustomFieldPayload = (source: any, store: any) => {
  const originalName: string = source?.label || source?.name || "";
  const formElements = store?.elements || [];
  const slideElements = (store?.slides || []).flatMap(
    (slide: any) => slide?.slideData || []
  );
  const customFields = store?.customFields || [];
  const formElementNames = formElements
    .map((el: any) => el?.label)
    .filter((n: any) => typeof n === "string") as string[];
  const slideNames = slideElements
    .map((el: any) => el?.label)
    .filter((n: any) => typeof n === "string") as string[];
  const existingNames = [...new Set([...formElementNames, ...slideNames])];
  const isPictureChoice = isPictureChoiceField(source);

  const originalFieldKey: string =
    source?.fieldKey || source?.hiddenFieldQueryKey || "";
  const [prefix, keyPart] = originalFieldKey?.includes(".")
    ? [originalFieldKey.split(".")[0], originalFieldKey.split(".")[1]]
    : ["", originalFieldKey];

  const existingFieldKeys = [...customFields, ...formElements, ...slideElements]
    .map((f: any) =>
      typeof f?.fieldKey === "string"
        ? f.fieldKey.split(".").pop()
        : f?.hiddenFieldQueryKey
    )
    .filter((k: any) => typeof k === "string") as string[];
  let name: string;
  let uniqueKeyPart: string;

  if (isPictureChoice) {
    const pictureChoiceCount = [
      ...customFields,
      ...formElements,
      ...slideElements,
    ].filter((field) => isPictureChoiceField(field)).length;
    const baseName = createDefaultPictureChoiceElement().label;

    do {
      name = `${baseName} ${pictureChoiceCount + 1}${getUniqueId().slice(
        0,
        3
      )}`;
      uniqueKeyPart = getConstructedQueryKey(name);
    } while (
      existingNames.includes(name) ||
      existingFieldKeys.includes(uniqueKeyPart)
    );
  } else {
    // Preserve the established copy identity for every existing custom field.
    const refreshedBaseName = refreshLabelThreeCharCode(originalName);
    name = getUniqueCopyName(refreshedBaseName, existingNames);
    uniqueKeyPart = getUniqueCopyHiddenKey(
      `${keyPart}_${getUniqueId().slice(0, 3)}`,
      existingFieldKeys
    );
  }

  const fieldKey = prefix ? `${prefix}.${uniqueKeyPart}` : uniqueKeyPart;

  const normalizeDataType = (dt: string) =>
    dt === "RATING" || dt === "SCORE" ? "NUMERICAL" : dt;

  const payload: any = {
    name,
    fieldKey,
    dataType: normalizeDataType(source?.dataType || source?.type),
    documentType: "field",
    showInForms: true,
    model: "contact",
    parentId: store?.parentFolderId || undefined,
    description: source?.description || "",
  };
  if (isPictureChoice) {
    Object.assign(payload, buildPictureChoiceCustomFieldPayload(source));
  }

  if (source?.dataType === "URL") {
    payload.urlValidation = normalizeUrlValidation(source?.urlValidation);
  }

  // Include options/extra attributes required by API for specific data types
  // Derive string options from picklistOptions, calculatedOptions, or fall back to picklistOptionsImage labels
  let stringOptions: string[] | undefined;
  if (Array.isArray(source?.picklistOptions)) {
    stringOptions = (source.picklistOptions as any[]).map((option) =>
      typeof option === "object" && option !== null
        ? (option as any).label
        : option
    ) as string[];
  } else if (Array.isArray((source as any)?.calculatedOptions)) {
    stringOptions = ((source as any).calculatedOptions as any[]).map((option) =>
      typeof option === "object" && option !== null
        ? (option as any).label
        : option
    ) as string[];
  } else if (Array.isArray((source as any)?.picklistOptionsImage)) {
    stringOptions = ((source as any).picklistOptionsImage as any[])
      .map((option) =>
        option && typeof option === "object" ? (option as any).label : option
      )
      .filter((v) => typeof v === "string") as string[];
  }

  if (source?.dataType === "TEXTBOX_LIST") {
    payload.textBoxListOptions = Array.isArray(source?.picklistOptions)
      ? (source?.picklistOptions as any[]).map((option: any) => {
          if (typeof option === "object" && option !== null) {
            const { value, ...rest } = option;
            return rest;
          }
          // Fallback: create minimal option object when only string provided
          return { label: String(option) };
        })
      : [];
  }

  if (
    !isPictureChoice &&
    ["SINGLE_OPTIONS", "MULTIPLE_OPTIONS", "CHECKBOX"].includes(
      source?.dataType
    )
  ) {
    payload.options =
      stringOptions && stringOptions.length > 0
        ? (stringOptions as string[])
        : [];
  }

  if (source?.dataType === "RADIO" && !isPictureChoice) {
    const imageOptionsRaw: any[] = Array.isArray(
      (source as any)?.picklistOptionsImage
    )
      ? ((source as any).picklistOptionsImage as any[])
      : [];
    const imageOptions = imageOptionsRaw.map((opt: any) => ({
      label: opt?.label,
      src: opt?.src,
    }));
    const hasImages = imageOptions.length > 0;
    payload.options = hasImages
      ? null
      : stringOptions && stringOptions.length > 0
      ? (stringOptions as string[])
      : [];
    if (hasImages) {
      (payload as any).picklistOptionsImage = imageOptions;
    }
    payload.allowCustomOption = (source as any)?.allowCustomOption;
  }

  if (source?.dataType === "FILE_UPLOAD") {
    const formats = Array.isArray(source?.picklistOptions)
      ? source.picklistOptions
      : [];
    payload.acceptedFormat =
      (formats as any[]).length === 0 ? ["all"] : formats;
    payload.isMultipleFile = (source as any)?.isMultipleFile;
    payload.maxNumberOfFiles = (source as any)?.isMultipleFile
      ? parseInt((source as any)?.maxFileSize)
      : undefined;
  }

  return { payload, uniqueKeyPart, name };
};

/**
 * Maps API customField response and original element into a new element clone for the builder.
 */
export const mapApiCustomFieldToElementClone = (
  source: any,
  apiField: any,
  defaultUniqueKeyPart: string
) => {
  return {
    ...source,
    id: apiField?.id,
    Id: apiField?.id,
    tag: apiField?.id,
    // Preserve clone-time label (includes "(copy)"), fall back to API name
    label: source?.label || apiField?.name || source?.name,
    fieldKey: apiField?.fieldKey,
    hiddenFieldQueryKey:
      (apiField?.fieldKey || "").split(".")[1] || defaultUniqueKeyPart,
    customEdited: true,
    custom: true,
  };
};

/**
 * Builds cloned slide data with locally inserted custom field clones.
 * Persistence is deferred until Save; no background API tasks are scheduled.
 *
 * @param sourceSlide - The original slide object containing slideData array
 * @param slideIndex - Index of the source slide in the store
 * @param store - Pinia store instance (must expose slides, locationId, addToHistory, anyUnsavedChanges)
 * @returns clonedSlideData and a no-op runner for compatibility with callers
 */
export const buildClonedSlideDataAndTasks = (
  sourceSlide: any,
  slideIndex: number,
  store: any
): {
  clonedSlideData: any[];
  runCustomFieldCreations: (targetSlideIndex: number) => Promise<void>;
} => {
  const existingKeys: string[] = (sourceSlide?.slideData || [])
    .map((e: any) => e?.hiddenFieldQueryKey)
    .filter((k: any) => typeof k === "string") as string[];

  const clonedSlideData = (sourceSlide?.slideData || []).map(
    (el: any, idx: number) => {
      // Treat elements with custom flag OR id/fieldKey/documentType as custom fields
      const isCustomField = Boolean(
        el?.custom ||
          el?.id !== undefined ||
          typeof el?.fieldKey === "string" ||
          el?.documentType === "field"
      );
      // Standard fields: local clone
      if (!isCustomField) {
        const clonedStd = cloneElementWithUniqueHiddenKey(el, existingKeys, {
          ensureUuid: true,
        });
        if (typeof clonedStd?.hiddenFieldQueryKey === "string") {
          existingKeys.push(clonedStd.hiddenFieldQueryKey);
        }
        return clonedStd;
      }

      // Custom field: local clone, mark for later persistence on Save
      const { payload, uniqueKeyPart, name } = buildClonedCustomFieldPayload(
        el,
        store
      );
      const preClone = cloneElementWithUniqueHiddenKey(el, existingKeys, {
        ensureUuid: true,
      });
      // Ensure label uses unique copy naming for existing custom fields too
      if (typeof preClone?.label === "string") {
        const formElementNames2 = (store?.elements || [])
          .map((e: any) => e?.label)
          .filter((n: any) => typeof n === "string") as string[];
        const slideNames2 = (store?.slides || [])
          .flatMap((s: any) => (s?.slideData || []).map((e: any) => e?.label))
          .filter((n: any) => typeof n === "string") as string[];
        const existingNames2 = [
          ...new Set([...formElementNames2, ...slideNames2]),
        ];
        preClone.label = getUniqueCopyName(preClone.label, existingNames2);
      }
      const tempHiddenKey = (payload.fieldKey as string).includes(".")
        ? (payload.fieldKey as string).split(".")[1]
        : (payload.fieldKey as string);
      const tempClone: any = {
        ...preClone,
        label: name,
        fieldKey: payload.fieldKey,
        hiddenFieldQueryKey: tempHiddenKey || uniqueKeyPart,
        custom: true,
        customEdited: false,
        __pendingClone: true,
        ...(payload.urlValidation
          ? { urlValidation: normalizeUrlValidation(payload.urlValidation) }
          : {}),
      };
      // Ensure clone is treated as a new custom field at save-time
      // - Do not carry over update flags
      // - Remove existing ids so save path uses create, not update
      // - Give a temporary unique tag to avoid collisions prior to save
      tempClone.customfieldUpdated = false;
      delete (tempClone as any).Id;
      delete (tempClone as any).id;
      (tempClone as any).tag = tempHiddenKey || uniqueKeyPart;
      // Ensure sidebar binds to editable label for pending clones
      delete (tempClone as any).customFieldLabel;
      delete (tempClone as any).name;
      if (typeof tempClone?.hiddenFieldQueryKey === "string") {
        existingKeys.push(tempClone.hiddenFieldQueryKey);
      }
      return tempClone;
    }
  );

  const runCustomFieldCreations = async (targetSlideIndex: number) => {
    return Promise.resolve();
  };

  return { clonedSlideData, runCustomFieldCreations };
};
/**
 * Fixes cursor position in contenteditable elements
 * This function forces the cursor to stay at the correct position for all browsers
 * @param e The event object
 * @param isKeyDown Boolean indicating if this was called from a keydown event
 */
export const fixSafariCursor = (e: Event, isKeyDown = false) => {
  // Skip cursor repositioning for navigation keys
  if (isKeyDown) {
    const keyEvent = e as KeyboardEvent;
    const navKeys = [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    const modifierKeys = [
      "Shift",
      "Control",
      "Alt",
      "Meta",
      "CapsLock",
      "NumLock",
      "ScrollLock",
      "Fn",
      "FnLock",
      "OS",
      "Tab",
    ];
    // Do not collapse selection for navigation, modifier keys,
    // or when using keyboard shortcuts (Cmd/Ctrl/Alt combos like copy/paste)
    if (
      navKeys.includes(keyEvent.key) ||
      modifierKeys.includes(keyEvent.key) ||
      keyEvent.metaKey ||
      keyEvent.ctrlKey ||
      keyEvent.altKey
    ) {
      return;
    }
  }

  const target = e.target as HTMLElement;
  const selection = window.getSelection();
  if (!selection?.rangeCount) {
    return;
  }

  // Store current position
  const range = selection.getRangeAt(0);

  // Skip if there's an actual selection (not just a cursor)
  // Let the browser handle text replacement naturally when user types over selected text
  if (!range.collapsed) {
    return;
  }

  const offset = range.endOffset;

  // Force redraw with correct cursor position
  nextTick(() => {
    const newRange = document.createRange();
    try {
      // Find the text node to position the cursor
      let textNode = null;
      if (target.firstChild && target.firstChild.nodeType === Node.TEXT_NODE) {
        textNode = target.firstChild;
      } else if (target.childNodes.length > 0) {
        // Search for the first text node
        for (let i = 0; i < target.childNodes.length; i++) {
          if (target.childNodes[i].nodeType === Node.TEXT_NODE) {
            textNode = target.childNodes[i];
            break;
          }
        }
      }

      if (!textNode) {
        // Create a text node if none exists
        textNode = document.createTextNode(target.textContent || "");
        target.textContent = "";
        target.appendChild(textNode);
      }

      // Ensure we don't exceed the text length
      const textLength = textNode.textContent?.length || 0;
      const safeOffset = Math.min(offset, textLength);

      newRange.setStart(textNode, safeOffset);
      newRange.setEnd(textNode, safeOffset);

      selection.removeAllRanges();
      selection.addRange(newRange);
    } catch (error) {
      logError("Failed to reposition cursor in contenteditable element", error);
    }
  });
};

export const getPriceStatement = (product: any) => {
  let priceStatement = "";
  if (product.type === "recurring") {
    if (product.recurring) {
      priceStatement =
        product.recurring.intervalCount > 1
          ? ` per ${product.recurring.intervalCount} ${product.recurring.interval}s`
          : `/${product.recurring.interval}`;
    } else {
      priceStatement = product.interval ? "/" + product.interval : "";
    }
  }
  return priceStatement;
};

/**
 * Smoothly scrolls the builder container by the height of a specific slide minus a provided offset.
 *
 * @param scrollerId - The id of the scrollable container (e.g., 'form-builder')
 * @param listId - The id of the element that contains the slide wrappers (e.g., 'form-builder-container')
 * @param slideSelector - CSS selector for slide wrappers (e.g., '.survey-form-container')
 * @param targetIndex - Index of the target slide in the DOM NodeList
 * @param subtractPx - Pixels to subtract from the measured height (default 20)
 */
export const scrollContainerBySlideHeight = (
  scrollerId: string,
  listId: string,
  slideSelector: string,
  targetIndex: number,
  subtractPx = 20
): void => {
  requestAnimationFrame(() => {
    const scroller = document.getElementById(scrollerId);
    const list = document.getElementById(listId);
    if (!scroller || !list) {
      return;
    }
    const slides = list.querySelectorAll(slideSelector);
    const target = slides.item(targetIndex) as HTMLElement | null;
    if (!target) {
      return;
    }
    const height =
      target.getBoundingClientRect().height ||
      (target as any).offsetHeight ||
      0;
    const offset = Math.max(0, height - subtractPx);
    if (offset > 0) {
      const el: any = scroller as any;
      if (typeof el.scrollBy === "function") {
        el.scrollBy({ top: offset, behavior: "smooth" });
      } else if (typeof el.scrollTo === "function") {
        el.scrollTo({
          top: (scroller as any).scrollTop + offset,
          behavior: "smooth",
        });
      } else {
        (scroller as any).scrollTop = (scroller as any).scrollTop + offset;
      }
    }
  });
};

/**
 * Get the caret offset within a contenteditable element.
 */
export const getCaretOffsetWithin = (element: HTMLElement): number => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return element.textContent ? element.textContent.length : 0;
  }
  const range = selection.getRangeAt(0);
  if (!element.contains(range.startContainer)) {
    return element.textContent ? element.textContent.length : 0;
  }
  const preRange = range.cloneRange();
  preRange.selectNodeContents(element);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString().length;
};

/**
 * Restore the caret to a specific offset within a contenteditable element.
 */
export const setCaretOffsetWithin = (
  element: HTMLElement | null,
  offset: number
): void => {
  if (!element) {
    return;
  }
  const doc = element.ownerDocument || document;
  const walker = doc.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let currentOffset = 0;
  let found = false;
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    const next = currentOffset + node.length;
    if (offset <= next) {
      const range = doc.createRange();
      range.setStart(node, Math.max(0, offset - currentOffset));
      range.collapse(true);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
      found = true;
      break;
    }
    currentOffset = next;
  }
  if (!found) {
    const range = doc.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }
};

/**
 * Shared handler for short-label contenteditable inputs.
 * Normalizes text, updates the bound ref, emits update, keeps caret stable, and applies Safari fixes.
 */
export const handleShortLabelInput = (
  e: Event,
  shortLabelRef: { value: string },
  updateShortLabel: () => void
) => {
  const target = e.target as HTMLElement;
  const caret = getCaretOffsetWithin(target);
  const text = (target.textContent || "").replace(/\u200B/g, "");
  shortLabelRef.value = text;
  updateShortLabel();
  fixSafariCursor(e, e.type === "keydown");
  nextTick(() => setCaretOffsetWithin(target, caret));
};

/**
 * @description Converts a number to its English ordinal form (1st, 2nd, 3rd, etc.)
 * @param {number} num - The number to convert
 * @returns {string} The ordinal form of the number
 */
export const getOrdinal = (num: number): string => {
  const j = num % 10;
  const k = num % 100;

  if (j === 1 && k !== 11) {
    return `${num}st`;
  }
  if (j === 2 && k !== 12) {
    return `${num}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${num}rd`;
  }
  return `${num}th`;
};
